/**
 * Plasma Field Alpine.js Component
 * Slow, softly drifting FBM field with a subtle mouse halo and gentle click ripple.
 * Brand palette: near-black base fading into muted indigo, with primary lime at peaks.
 */

import { ShaderRenderer } from './utils/shader-renderer.js';
import { observeFooterReveal } from './utils/footer-reveal.js';

// GLSL template literal — do not add backticks or ${ to this string without escaping.
const FRAG = `
precision highp float;
uniform vec2  u_res;
uniform float u_time;
uniform vec2  u_mouse;
uniform vec2  u_click;
uniform float u_clickTime;

float hash12(vec2 p){ vec3 p3 = fract(vec3(p.xyx)*.1031); p3 += dot(p3, p3.yzx+33.33); return fract((p3.x+p3.y)*p3.z); }

float noise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  vec2 u = f*f*(3.0-2.0*f);
  return mix(mix(hash12(i+vec2(0,0)), hash12(i+vec2(1,0)), u.x),
             mix(hash12(i+vec2(0,1)), hash12(i+vec2(1,1)), u.x), u.y);
}
float fbm(vec2 p){
  float v=0.0, a=0.5;
  for(int i=0;i<5;i++){ v+=a*noise(p); p*=2.02; a*=0.5; }
  return v;
}

void main(){
  vec2 uv = (gl_FragCoord.xy - 0.5*u_res) / min(u_res.x, u_res.y);
  vec2 m  = (u_mouse - 0.5) * vec2(u_res.x/min(u_res.x,u_res.y), u_res.y/min(u_res.x,u_res.y));

  float t = u_time * 0.07;
  vec2 p = uv * 1.4;

  // Single-pass domain warp — small amplitude keeps motion slow and organic.
  vec2 q = vec2(fbm(p + vec2(0.0, t)),
                fbm(p + vec2(5.1, 1.3) - t));
  float v = fbm(p + 1.1*q);

  // Click shockwave — quiet ripple added to the field scalar.
  float clickAge = u_time - u_clickTime;
  if(clickAge < 2.5){
    vec2 cp = (u_click - 0.5) * vec2(u_res.x/min(u_res.x,u_res.y), u_res.y/min(u_res.x,u_res.y));
    float cd = length(uv - cp);
    v += sin(cd*9.0 - clickAge*4.5) * exp(-cd*2.0) * exp(-clickAge*1.2) * 0.06;
  }

  vec3 cBase   = vec3(0.060, 0.062, 0.082);
  vec3 cIndigo = vec3(0.160, 0.175, 0.320);
  vec3 cGreen  = vec3(0.364, 0.840, 0.000);

  vec3 col = cBase;
  col = mix(col, cIndigo, smoothstep(0.25, 0.72, v) * 0.82);
  col = mix(col, cGreen,  smoothstep(0.57, 0.92, v) * 0.65);

  // Gentle cursor halo — luminance only, no warp.
  float mDist = length(uv - m);
  col += cIndigo * exp(-mDist*mDist*3.0) * 0.35;

  // Soft vignette for depth.
  col *= 0.82 + 0.18 * smoothstep(1.20, 0.30, length(uv));

  gl_FragColor = vec4(col, 1.0);
}
`;

export default () => ({
    renderer: null,

    init() {
        if (!window.matchMedia('(min-width: 768px)').matches) return;
        const canvas = this.$refs.canvas;
        if (!canvas) return;

        try {
            this.renderer = new ShaderRenderer(canvas);
            this.renderer.setShader(FRAG);
            this.$el.classList.remove('opacity-0');
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                this.renderer.frame();
            } else {
                this.stopObservingFooter = observeFooterReveal((revealed) => revealed ? this.renderer?.start() : this.renderer?.stop());
            }
        } catch (e) {
            this.renderer = null;
        }
    },

    destroy() {
        this.stopObservingFooter?.();
        this.renderer?.destroy();
        this.renderer = null;
    },
});
